import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  InputOTP, InputOTPGroup, InputOTPSlot,
} from "@/components/ui/input-otp";
import { toast } from "sonner";
import { Mail, Download, ArrowRight, PackageSearch, FileDown } from "lucide-react";
import CatalogView from "@/components/CatalogView";

const HERO =
  "https://images.unsplash.com/photo-1620203853151-496c7228306c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxzdGFpbmxlc3MlMjBzdGVlbCUyMGluZHVzdHJpYWwlMjBtYWNoaW5lfGVufDB8fHx8MTc4Nzg1MTU3NHww&ixlib=rb-4.1.0&q=85";

export default function CustomerPortal() {
  const { token: routeToken } = useParams();
  const universal = !routeToken;
  const [stage, setStage] = useState(universal ? "email" : "loading"); // loading|notfound|email|code|catalog
  const [info, setInfo] = useState(null);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(null);
  const [token, setToken] = useState(routeToken || null); // active token used for catalog/file/export calls
  const [catalog, setCatalog] = useState(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    axios.get(`${API}/portal-settings`).then((r) => setMessage(r.data.message || "")).catch(() => {});
  }, []);

  useEffect(() => {
    if (universal) return;
    axios.get(`${API}/portal/${routeToken}`)
      .then((r) => { setInfo(r.data); setStage("email"); })
      .catch(() => setStage("notfound"));
  }, [routeToken, universal]);

  const requestCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (universal) await axios.post(`${API}/portal-shared/request-otp`, { email });
      else await axios.post(`${API}/portal/${routeToken}/request-otp`, { email });
      toast.success("If that email matches, a code is on its way.");
      setStage("code");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const verify = async (val) => {
    setBusy(true);
    try {
      let data, activeTok;
      if (universal) {
        ({ data } = await axios.post(`${API}/portal-shared/verify-otp`, { email, code: val }));
        activeTok = data.token;
        setToken(activeTok);
      } else {
        ({ data } = await axios.post(`${API}/portal/${routeToken}/verify-otp`, { email, code: val }));
        activeTok = routeToken;
      }
      setSession(data.session_token);
      const cat = await axios.get(`${API}/portal/${activeTok}/catalog`, { headers: { Authorization: `Bearer ${data.session_token}` } });
      setCatalog(cat.data);
      setStage("catalog");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Invalid code");
      setCode("");
    } finally { setBusy(false); }
  };

  const exportCsv = useCallback(async (family) => {
    try {
      const r = await axios.get(`${API}/portal/${token}/export?family=${encodeURIComponent(family)}&t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${session}` }, responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `thermaco_pricing_${family}.csv`.replace(/\s+/g, "_");
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  }, [token, session]);

  const exportPdf = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/portal/${token}/export.pdf?t=${Date.now()}`, { headers: { Authorization: `Bearer ${session}` }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "thermaco_price_list.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF download failed"); }
  }, [token, session]);

  if (stage === "loading")
    return <div className="min-h-screen grid place-items-center bg-slate-50 text-slate-400 font-body">Loading…</div>;

  if (stage === "notfound")
    return (
      <div className="min-h-screen grid place-items-center bg-slate-900 text-white px-6">
        <div className="text-center max-w-md">
          <PackageSearch className="h-12 w-12 mx-auto text-slate-500" />
          <h1 className="font-heading text-2xl font-bold mt-4">Link not available</h1>
          <p className="text-slate-400 mt-2">This pricing link is invalid, disabled, or has been replaced. Please contact your Thermaco representative for an updated link.</p>
        </div>
      </div>
    );

  if (stage === "email" || stage === "code")
    return (
      <div className="min-h-screen relative grid place-items-center px-6">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/80" />
        <div className="relative w-full max-w-md bg-white rounded-sm p-8 shadow-xl">
          <div className="flex items-center gap-2 mb-6">
            <img src="/thermaco-logo.webp" alt="Thermaco" className="h-8" />
          </div>
          {message && (
            <div className="mb-6 rounded-sm border-l-2 border-blue-600 bg-blue-50 px-4 py-3 text-sm text-slate-700 whitespace-pre-line" data-testid="portal-welcome-message">
              {message}
            </div>
          )}
          {stage === "email" ? (
            <>
              <h1 className="font-heading text-2xl font-bold text-slate-900">Welcome{info?.customer_name ? `, ${info.customer_name}` : ""}</h1>
              <p className="text-sm text-slate-500 mt-1 mb-6">Enter your email to receive a one-time access code and view your pricing.</p>
              <form onSubmit={requestCode} className="space-y-4" data-testid="otp-email-form">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] text-slate-500">Email address</Label>
                  <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder={info?.email_hint || "you@company.com"} className="rounded-sm" data-testid="otp-email-input" />
                </div>
                <Button type="submit" disabled={busy} className="w-full rounded-sm bg-slate-900 hover:bg-slate-800 transition-colors" data-testid="otp-request-button">
                  <Mail className="h-4 w-4 mr-2" /> {busy ? "Sending…" : "Send access code"}
                </Button>
              </form>
            </>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-bold text-slate-900">Enter your code</h1>
              <p className="text-sm text-slate-500 mt-1 mb-6">We sent a 6-digit code to {info?.email_hint || "your email"}. It expires in 10 minutes.</p>
              <div className="flex justify-center" data-testid="otp-code-input">
                <InputOTP maxLength={6} value={code} onChange={(v) => { setCode(v); if (v.length === 6) verify(v); }}>
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="rounded-sm h-12 w-11 text-lg" />)}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button disabled={busy || code.length < 6} onClick={() => verify(code)} className="w-full mt-6 rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="otp-verify-button">
                {busy ? "Verifying…" : "View my pricing"} <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <button onClick={() => { setStage("email"); setCode(""); }} className="w-full text-center text-sm text-slate-500 hover:text-slate-900 mt-3 transition-colors">
                Use a different email
              </button>
            </>
          )}
        </div>
        <a href="/login" className="relative mt-4 text-xs text-white/70 hover:text-white transition-colors" data-testid="staff-signin-link">
          Staff sign-in
        </a>
      </div>
    );

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/thermaco-logo.webp" alt="Thermaco" className="h-8" />
            <div>
              <div className="font-heading font-bold tracking-tight text-slate-900 leading-none text-sm">Pricing</div>
              <div className="text-xs text-slate-500 mt-0.5" data-testid="portal-customer-line">{catalog.customer_name}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={exportPdf} variant="outline" className="rounded-sm transition-colors" data-testid="export-pdf-button">
              <FileDown className="h-4 w-4 mr-2" /> Download PDF
            </Button>
            <Button onClick={() => exportCsv("all")} className="rounded-sm bg-blue-600 hover:bg-blue-700 transition-colors" data-testid="export-all-button">
              <Download className="h-4 w-4 mr-2" /> Export all (CSV)
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <CatalogView catalog={catalog} fileUrl={(ref) => `${API}/portal/${token}/file/${ref}?auth=${session}`} onExport={exportCsv} />
      </main>
    </div>
  );
}
